import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, Paper, Divider } from '@mui/material';
import { Error as ErrorIcon, BugReport, Token } from '@mui/icons-material';

const UnauthorizedPage = () => {
  const navigate = useNavigate();
  const [debugInfo, setDebugInfo] = useState(null);
  
  const checkAuthStatus = () => {
    // Get user from localStorage
    const token = localStorage.getItem('token');
    const userString = localStorage.getItem('user');
    let tokenPayload = null;
    
    // Decode token if exists
    if (token) {
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          tokenPayload = JSON.parse(atob(tokenParts[1]));
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
    
    // Parse user if exists
    let user = null;
    if (userString) {
      try {
        user = JSON.parse(userString);
      } catch (error) {
        console.error('Error parsing user JSON:', error);
      }
    }
    
    setDebugInfo({
      hasToken: !!token,
      tokenExpiry: tokenPayload ? new Date(tokenPayload.exp * 1000).toLocaleString() : 'N/A',
      tokenRole: tokenPayload?.role || 'none',
      hasUserInStorage: !!userString,
      userEmail: user?.email || 'none',
      userRole: user?.role || 'none'
    });
  };
  
  const fixAuthData = () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      alert('No token found. Please login again.');
      navigate('/login');
      return;
    }
    
    try {
      // Decode token and extract role
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        alert('Invalid token format');
        return;
      }
      
      const payload = JSON.parse(atob(tokenParts[1]));
      
      // Get current user data
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : {};
      
      // Update with role from token
      const updatedUser = {
        ...user,
        role: payload.role
      };
      
      // Save back to localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      alert('Fixed user data. Role added from token. Try accessing admin page again.');
      navigate('/admin/invitations');
    } catch (error) {
      console.error('Error fixing auth data:', error);
      alert('Error fixing auth data: ' + error.message);
    }
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          py: 4
        }}
      >
        <Paper 
          elevation={3}
          sx={{
            p: 5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2
          }}
        >
          <ErrorIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
          
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Access Denied
          </Typography>
          
          <Typography variant="body1" paragraph>
            You don't have permission to access this page.
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            This area is restricted to authorized personnel only. If you believe you should have access, please contact your administrator.
          </Typography>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              component={Link}
              to="/"
              variant="contained"
              color="primary"
            >
              Return to Home
            </Button>
            
            <Button
              component={Link}
              to="/login"
              variant="outlined"
            >
              Login Again
            </Button>
          </Box>
          
          <Divider sx={{ width: '100%', my: 4 }} />
          
          {/* Debug section */}
          <Typography variant="h6" color="text.secondary">
            Debug Tools
          </Typography>
          
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="info" 
              startIcon={<BugReport />}
              onClick={checkAuthStatus}
            >
              Check Auth Status
            </Button>
            
            <Button 
              variant="outlined" 
              color="warning" 
              startIcon={<Token />}
              onClick={fixAuthData}
            >
              Fix Auth Data
            </Button>
          </Box>
          
          {debugInfo && (
            <Paper sx={{ mt: 3, p: 2, width: '100%', textAlign: 'left', bgcolor: 'grey.100' }}>
              <Typography variant="subtitle2" fontWeight="bold">Authentication Debug Info:</Typography>
              <pre style={{ overflow: 'auto' }}>
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </Paper>
          )}
          
          <Typography variant="h3" component="h1" gutterBottom>
            Access Denied
          </Typography>
          
          <Typography variant="h6" color="text.secondary" paragraph>
            You don't have permission to access this page.
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            This area is restricted to authorized personnel only. 
            If you believe you should have access, please contact your administrator.
          </Typography>
          
          <Box sx={{ mt: 4 }}>
            <Button 
              component={Link} 
              to="/"
              variant="contained" 
              color="primary" 
              size="large"
              sx={{ mr: 2 }}
            >
              Go to Home
            </Button>
            
            <Button 
              component={Link} 
              to="/login"
              variant="outlined" 
              color="primary" 
              size="large"
            >
              Login
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default UnauthorizedPage;
